import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Storage helper functions
export const storageHelpers = {
  // Upload image to storage
  uploadImage: async (file: File, bucket: string = 'space-images') => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        return { data: null, error };
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      return { data: { path: filePath, publicUrl }, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },

  // Delete image from storage
  deleteImage: async (filePath: string, bucket: string = 'space-images') => {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([filePath]);
    return { error };
  },

  // Upload multiple images
  uploadMultipleImages: async (files: File[], bucket: string = 'space-images') => {
    const uploadPromises = files.map(file => storageHelpers.uploadImage(file, bucket));
    const results = await Promise.all(uploadPromises);
    
    const successfulUploads = results
      .filter(result => !result.error && result.data)
      .map(result => result.data!.publicUrl);
    
    const errors = results.filter(result => result.error);
    
    return { 
      data: successfulUploads, 
      error: errors.length > 0 ? errors[0].error : null 
    };
  }
};

// Auth helper functions
export const authHelpers = {
  // Sign up with email
  signUp: async (email: string, password: string, fullName: string, phone: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          phone: phone,
        },
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) throw error;

    // Kullanıcı oluşturulduysa profil kaydını da oluştur
    if (data.user) {
      // Profil kaydını oluştur
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([
          {
            id: data.user.id,
            full_name: fullName,
            phone: phone,
            email: email,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ]);

      if (profileError) {
        console.error('Profil oluşturma hatası:', profileError);
        // Profil hatası olsa bile kayıt devam etsin
      }
    }

    return data;
  },

  // Sign in with email
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  // Sign in with Google
  signInWithGoogle: async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { data, error };
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current user
  getCurrentUser: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  // Get session
  getSession: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  // Check if user is admin
  checkIsAdmin: async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', userId)
      .single();
    
    if (error || !data) {
      return false;
    }
    
    return data.is_admin === true;
  },
};

// Database helper functions
export const dbHelpers = {
  // Create or update profile
  upsertProfile: async (userId: string, profileData: any) => {
    const { data, error } = await supabase
      .from('profiles')
      .upsert({
        id: userId,
        ...profileData,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();
    return { data, error };
  },

  // Get profile
  getProfile: async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },

  // Get all profiles (admin only)
  getAllProfiles: async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Update user admin status
  updateUserAdminStatus: async (userId: string, isAdmin: boolean) => {
    const { data, error } = await supabase
      .from('profiles')
      .update({ is_admin: isAdmin, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  },

  // Create space listing
  createSpace: async (spaceData: any) => {
    const user = await authHelpers.getCurrentUser();
    if (!user) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('spaces')
      .insert({
        user_id: user.id,
        ...spaceData,
      })
      .select()
      .single();
    return { data, error };
  },

  // Get user's spaces
  getUserSpaces: async () => {
    const user = await authHelpers.getCurrentUser();
    if (!user) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('spaces')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Get all active spaces
  getActiveSpaces: async () => {
    const { data, error } = await supabase
      .from('spaces')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Update space
  updateSpace: async (spaceId: string, spaceData: any) => {
    const { data, error } = await supabase
      .from('spaces')
      .update({
        ...spaceData,
        updated_at: new Date().toISOString(),
      })
      .eq('id', spaceId)
      .select()
      .single();
    return { data, error };
  },

  // Delete space
  deleteSpace: async (spaceId: string) => {
    const { data, error } = await supabase
      .from('spaces')
      .delete()
      .eq('id', spaceId);
    return { data, error };
  },
};
